/**
 * Wing-sectioning MVP V5 surface (FXC-9009 / blown-lift × Tailor — "slice wings,
 * add 2D at stations, carve out to 3D").
 *
 * The 3-step MVP workflow, honest about what is and isn't wired:
 *   1. SLICE  — edit a trapezoidal planform (root/tip chord + LE sweep) in an inline
 *               SVG sketch, drop/drag eta-station markers (0..1), then POST
 *               /wing/slice to materialise the spanwise sections.
 *   2. SECTION— for any sliced station, open the native (no-iframe) high-lift foil
 *               tool seeded at that eta and edit the station's 2D design, then
 *               upsert it back as the section's design (+ a design_path link).
 *   3. CARVE  — per-eta flap deploy angles -> POST /wing/carve. The true 3D loft is
 *               intentionally DEFERRED: the backend reports loft_status
 *               "not_implemented", and we surface that honestly ("sections staged")
 *               rather than implying a STEP body was produced.
 *
 * Seam used to seed the foil tool from a section eta: a section persists its full
 * `HighLiftAirfoil` config under SectionInput.design (key DESIGN_CONFIG_KEY). On
 * "Open in foil tool" we reconstruct that config as the `useFoilDesign(seed)` seed;
 * if a row has no stored config we fall back to the default geometry, then nudge
 * the flap-deploy operation axis via `applyAxesToConfig({ flap_deploy_deg })` so the
 * station opens already deployed to whatever the slice/carve panel implied. On
 * "Save section" the live config is written straight back into design, so re-opening
 * the row restores exactly what the user last edited.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  DEFAULT_HIGH_LIFT_AIRFOIL,
  FOIL_VIEW,
  isDefaultCollectionId,
  type HighLiftAirfoil,
  type ViewRect,
} from "../highlift/geometry";
import { FoilGeometryCanvas } from "../highlift/foil/FoilGeometryCanvas";
import { ShapePanel, type ControlSurface } from "../highlift/workbench/ShapePanel";
import { applyAxesToConfig, useFoilDesign } from "../highlift/foil/useFoilDesign";
import { useHighliftTheme, type HighliftPalette } from "../highlift/foil/useHighliftTheme";
import { isUuidLike } from "../thread-v4/viewSpecs";
import {
  carveWing,
  cutControlSurface,
  listSections,
  sectionEtaKey,
  sliceWing,
  upsertSections,
  type CarveResponse,
  type CutMode,
  type CutResponse,
  type SectionSummary,
  type SliceResponse,
  type WingPlanform,
} from "../../api/wing";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import { WingViewer3D } from "./WingViewer3D";

// ---------------------------------------------------------------------------
// Constants / defaults
// ---------------------------------------------------------------------------

/** Key under SectionInput.design where we persist the full foil config (the seam). */
const DESIGN_CONFIG_KEY = "highlift_airfoil";

/** A sensible starting trapezoidal planform (metres). Editable in the SVG sketch. */
const DEFAULT_PLANFORM: WingPlanform = {
  root_le_x: 0.0,
  tip_le_x: 1.2,
  root_chord: 2.4,
  tip_chord: 1.0,
  span: 8.0,
  y_root: 0.0,
};

const DEFAULT_ETA_STATIONS = [0.15, 0.45, 0.75];
const DEFAULT_XC_HINGE = 0.7;
const DEFAULT_N_EXTRACT = 21;
const DEFAULT_N_LOFT = 10;

// ---------------------------------------------------------------------------
// Small styling helpers (inline, matching the foil-tool idiom — no shared CSS).
// Built from the live light/dark palette so the whole surface flips with the
// global theme toggle.
// ---------------------------------------------------------------------------

const FONT_STACK = "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
const MONO_STACK = "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace";

interface WingStyles {
  panelStyle: React.CSSProperties;
  sectionTitleStyle: React.CSSProperties;
  buttonStyle: React.CSSProperties;
  ghostButtonStyle: React.CSSProperties;
  fieldLabelStyle: React.CSSProperties;
  numberInputStyle: React.CSSProperties;
  thtdStyle: React.CSSProperties;
  thStyle: React.CSSProperties;
}

function buildStyles(palette: HighliftPalette): WingStyles {
  const panelStyle: React.CSSProperties = {
    background: palette.surface,
    border: `1px solid ${palette.line}`,
    borderRadius: 8,
    padding: "12px 16px 16px",
    fontFamily: FONT_STACK,
    color: palette.ink,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: palette.ink,
    fontFamily: FONT_STACK,
    margin: "0 0 8px",
    paddingBottom: 4,
    borderBottom: `1px solid ${palette.line}`,
  };

  const buttonStyle: React.CSSProperties = {
    background: palette.primarySoft,
    color: palette.blue,
    border: `1px solid ${palette.blue}`,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontFamily: FONT_STACK,
    cursor: "pointer",
  };

  const ghostButtonStyle: React.CSSProperties = {
    background: "transparent",
    color: palette.blue,
    border: `1px solid ${palette.line}`,
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 11,
    fontFamily: FONT_STACK,
    cursor: "pointer",
  };

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: 11,
    color: palette.ink3,
    fontFamily: FONT_STACK,
    display: "block",
    marginBottom: 3,
  };

  const numberInputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    background: palette.surface2,
    color: palette.ink,
    border: `1px solid ${palette.line}`,
    borderRadius: 5,
    padding: "4px 6px",
    fontSize: 12,
    fontFamily: MONO_STACK,
  };

  const thtdStyle: React.CSSProperties = {
    fontSize: 12,
    fontFamily: MONO_STACK,
    color: palette.ink,
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: `1px solid ${palette.line}`,
    whiteSpace: "nowrap",
  };

  const thStyle: React.CSSProperties = {
    ...thtdStyle,
    color: palette.ink3,
    fontFamily: FONT_STACK,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  return {
    panelStyle,
    sectionTitleStyle,
    buttonStyle,
    ghostButtonStyle,
    fieldLabelStyle,
    numberInputStyle,
    thtdStyle,
    thStyle,
  };
}

// ---------------------------------------------------------------------------
// Planform sketch (inline SVG)
// ---------------------------------------------------------------------------

interface PlanformSketchProps {
  planform: WingPlanform;
  etaStations: number[];
  onChangeEta: (index: number, eta: number) => void;
  palette: HighliftPalette;
}

/**
 * Top-down trapezoidal-planform sketch. Spanwise (y) runs left→right; chordwise (x)
 * runs top→bottom. Eta markers sit on the chordline at each station and drag along
 * span (0..1). LE is the swept top edge (root_le_x → tip_le_x), TE the bottom edge.
 */
function PlanformSketch({ planform, etaStations, onChangeEta, palette }: PlanformSketchProps) {
  const W = 520;
  const H = 200;
  const pad = 24;
  const { root_le_x, tip_le_x, root_chord, tip_chord, span } = planform;

  // x-extent (chordwise) across both stations, for vertical scaling.
  const xMin = Math.min(root_le_x, tip_le_x);
  const xMax = Math.max(root_le_x + root_chord, tip_le_x + tip_chord);
  const xSpan = Math.max(xMax - xMin, 1e-6);
  const safeSpan = Math.max(span, 1e-6);

  const sx = (y: number) => pad + (y / safeSpan) * (W - 2 * pad); // span → px
  const sy = (x: number) => pad + ((x - xMin) / xSpan) * (H - 2 * pad); // chord → px

  // Planform corners (root at y=0, tip at y=span).
  const rootLe: [number, number] = [sx(0), sy(root_le_x)];
  const rootTe: [number, number] = [sx(0), sy(root_le_x + root_chord)];
  const tipLe: [number, number] = [sx(safeSpan), sy(tip_le_x)];
  const tipTe: [number, number] = [sx(safeSpan), sy(tip_le_x + tip_chord)];
  const poly = `${rootLe[0]},${rootLe[1]} ${tipLe[0]},${tipLe[1]} ${tipTe[0]},${tipTe[1]} ${rootTe[0]},${rootTe[1]}`;

  const leAtEta = (eta: number) => root_le_x + (tip_le_x - root_le_x) * eta;
  const chordAtEta = (eta: number) => root_chord + (tip_chord - root_chord) * eta;

  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const etaFromClientX = useCallback(
    (clientX: number, target: SVGSVGElement): number => {
      const rect = target.getBoundingClientRect();
      const px = ((clientX - rect.left) / rect.width) * W;
      const eta = (px - pad) / (W - 2 * pad);
      return Math.min(1, Math.max(0, eta));
    },
    [],
  );

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
      onPointerMove={(e) => {
        if (dragIdx == null) return;
        onChangeEta(dragIdx, etaFromClientX(e.clientX, e.currentTarget));
      }}
      onPointerUp={() => setDragIdx(null)}
      onPointerLeave={() => setDragIdx(null)}
    >
      {/* planform body */}
      <polygon points={poly} fill={palette.primarySoft} stroke={palette.blue} strokeWidth={1.4} />
      {/* LE / TE labels */}
      <text x={rootLe[0] + 4} y={rootLe[1] - 6} fontSize={10} fill={palette.ink3} fontFamily={FONT_STACK}>
        root
      </text>
      <text x={tipLe[0] - 24} y={tipLe[1] - 6} fontSize={10} fill={palette.ink3} fontFamily={FONT_STACK}>
        tip
      </text>
      {/* eta-station chordlines + draggable markers */}
      {etaStations.map((eta, i) => {
        const x = sx(eta * safeSpan);
        const yLe = sy(leAtEta(eta));
        const yTe = sy(leAtEta(eta) + chordAtEta(eta));
        return (
          <g key={i}>
            <line x1={x} y1={yLe} x2={x} y2={yTe} stroke={palette.reattach} strokeWidth={1.2} strokeDasharray="3 3" />
            <circle
              cx={x}
              cy={(yLe + yTe) / 2}
              r={6}
              fill={palette.reattach}
              stroke={palette.bg}
              strokeWidth={1.5}
              style={{ cursor: "ew-resize" }}
              onPointerDown={(e) => {
                (e.target as Element).setPointerCapture?.(e.pointerId);
                setDragIdx(i);
              }}
            />
            <text x={x + 8} y={yLe - 4} fontSize={10} fill={palette.reattach} fontFamily={MONO_STACK}>
              η{eta.toFixed(2)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Section foil editor (owns its own useFoilDesign — mounted only when open)
// ---------------------------------------------------------------------------

interface SectionFoilEditorProps {
  section: SectionSummary;
  /** Stored foil config for this section (the seam), or null to seed the default. */
  seedConfig: HighLiftAirfoil | null;
  /** Default flap-deploy nudge applied on top of the seed when no config is stored. */
  seedDeployDeg?: number;
  saving: boolean;
  /** Writes allowed only for a persisted (UUID) project — gates "Save section". */
  writable: boolean;
  onSave: (config: HighLiftAirfoil) => void;
  onClose: () => void;
  /** Reports the live foil config up so the 3-D viewer rebuilds as it's edited. */
  onConfigChange: (config: HighLiftAirfoil) => void;
  palette: HighliftPalette;
  styles: WingStyles;
}

/**
 * The native high-lift foil tool, seeded at one section's eta. Hooks can't be
 * called conditionally, so this lives in its own component mounted only when a row
 * is open — `useFoilDesign(seed)` then owns the live design for that station.
 */
function SectionFoilEditor({
  section,
  seedConfig,
  seedDeployDeg,
  saving,
  writable,
  onSave,
  onClose,
  onConfigChange,
  palette,
  styles,
}: SectionFoilEditorProps) {
  const { panelStyle, sectionTitleStyle, buttonStyle, ghostButtonStyle } = styles;
  // Build the seed once: stored config wins; else default + flap-deploy nudge.
  const seed = useMemo<HighLiftAirfoil>(() => {
    if (seedConfig) return seedConfig;
    const base = structuredClone(DEFAULT_HIGH_LIFT_AIRFOIL);
    if (seedDeployDeg != null && Number.isFinite(seedDeployDeg)) {
      applyAxesToConfig(base, { flap_deploy_deg: seedDeployDeg });
    }
    return base;
  }, [seedConfig, seedDeployDeg]);

  const foil = useFoilDesign(seed);

  // Stream the live design up so the 3-D wing viewer rebuilds as the foil is edited.
  useEffect(() => {
    onConfigChange(foil.config);
  }, [foil.config, onConfigChange]);

  // Workbench design-surface state: selected element (drives the Shape sliders + canvas handle
  // emphasis), the Qiqi-slotted ↔ plain-flap mode, and the 2-D canvas view (pan/zoom).
  const [selectedId, setSelectedId] = useState<string>("main");
  const [controlSurface, setControlSurface] = useState<ControlSurface>("qiqi");
  const [foilView, setFoilView] = useState<ViewRect>(FOIL_VIEW);

  return (
    <div
      style={{
        ...panelStyle,
        background: palette.surface2,
        marginTop: 8,
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.5fr) minmax(260px, 1fr)",
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ ...sectionTitleStyle, marginBottom: 6 }}>
          Foil tool · η {section.eta.toFixed(3)}
        </div>
        <FoilGeometryCanvas
          config={foil.config}
          configuration={foil.configuration}
          showAnnotations
          showLabels
          view={foilView}
          onViewChange={setFoilView}
          onSetControlPoint={foil.setHandleDescriptor}
          selectedElementId={selectedId}
          onSelectElement={setSelectedId}
          width={520}
          height={300}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            type="button"
            style={{ ...buttonStyle, opacity: saving || !writable ? 0.6 : 1 }}
            disabled={saving || !writable}
            onClick={() => onSave(foil.config)}
          >
            {saving ? "Saving…" : "Save section"}
          </button>
          <button type="button" style={ghostButtonStyle} onClick={onClose}>
            Close
          </button>
          <button type="button" style={ghostButtonStyle} onClick={foil.reset}>
            Reset
          </button>
        </div>
      </div>
      <div style={{ minWidth: 0, maxHeight: 380, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Elements — the workbench collection. Selecting one drives the Shape sliders + the canvas
            handle emphasis; "+ foil"/"+ blowing" add free-form elements / actuator disks. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {[
            ...foil.elements.map((el) => ({
              id: el.id, label: el.label, tag: el.kind, round: 2,
              dot: el.kind === "main-cove" ? palette.mainEdge : el.kind === "vane-bspline" ? palette.vaneEdge : palette.flapEdge,
            })),
            ...foil.blowing.map((b) => ({ id: b.id, label: b.label, tag: `Ø${b.diameter.toFixed(2)}`, round: 8, dot: palette.blue })),
          ].map((row) => (
            <div
              key={row.id}
              onClick={() => setSelectedId(row.id)}
              style={{
                display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: palette.ink3,
                padding: "5px 7px", borderRadius: 7, cursor: "pointer",
                border: `1px solid ${selectedId === row.id ? palette.primary : palette.line}`,
                background: selectedId === row.id ? palette.primarySoft : palette.surface,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: row.round, flex: "0 0 auto", background: row.dot }} />
              <span style={{ flex: 1, color: palette.ink }}>{row.label}</span>
              <span>{row.tag}</span>
              {isDefaultCollectionId(row.id) ? null : (
                <button
                  type="button"
                  style={{ ...ghostButtonStyle, padding: "0 6px" }}
                  title="remove"
                  onClick={(e) => { e.stopPropagation(); if (selectedId === row.id) setSelectedId("main"); foil.removeCollectionItem(row.id); }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
            <button type="button" style={{ ...ghostButtonStyle, flex: 1 }} onClick={foil.addVane} title="Add a free-form vane element">+ foil</button>
            <button type="button" style={{ ...ghostButtonStyle, flex: 1 }} onClick={foil.addBlowing} title="Add an actuator-disk blowing source">+ blowing</button>
          </div>
        </div>
        {/* Shape — the workbench per-element sliders + the Qiqi-slotted ↔ xfoil-plain-flap toggle. */}
        <ShapePanel
          palette={palette}
          config={foil.config}
          setters={foil.setters}
          selectedId={selectedId}
          controlSurface={controlSurface}
          onControlSurface={setControlSurface}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Pull the stored foil config out of a SectionSummary, if the seam wrote one. */
function storedConfigFor(section: SectionSummary, designs: Record<string, HighLiftAirfoil>): HighLiftAirfoil | null {
  return designs[section.path] ?? null;
}

function readNumber(value: string, fallback: number): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

/** True when the carve response honestly has no 3D body yet. */
function loftNotImplemented(carve: CarveResponse): boolean {
  if (!carve.feature_enabled) return true;
  if (carve.step_path) return false;
  const note = (carve.note ?? "").toLowerCase();
  return note.includes("not_implemented") || note.includes("not implemented") || !carve.step_path;
}

// ---------------------------------------------------------------------------
// Surface
// ---------------------------------------------------------------------------

export function WingSectioningSurface(props: SurfaceProps) {
  const { projectId, branch } = props;
  // Wing slice/section/carve POST to UUID-typed routes and persist server-side
  // nodes, so they need a real (persisted) project. A non-UUID projectId — an
  // unresolved slug or a demo fixture — can't carry wing nodes, so writes are
  // disabled with a clear note rather than failing with a raw 422.
  const persisted = isUuidLike(projectId);
  const queryClient = useQueryClient();
  const palette = useHighliftTheme();
  const styles = useMemo(() => buildStyles(palette), [palette]);
  const {
    panelStyle,
    sectionTitleStyle,
    buttonStyle,
    ghostButtonStyle,
    fieldLabelStyle,
    numberInputStyle,
    thtdStyle,
    thStyle,
  } = styles;

  // -- planform + station editor state --------------------------------------
  const [planform, setPlanform] = useState<WingPlanform>(DEFAULT_PLANFORM);
  const [etaStations, setEtaStations] = useState<number[]>(DEFAULT_ETA_STATIONS);
  const [xcHinge, setXcHinge] = useState<number>(DEFAULT_XC_HINGE);
  const [nExtract, setNExtract] = useState<number>(DEFAULT_N_EXTRACT);
  // -- CAD source: slice an arbitrary STEP instead of the parametric planform
  const [stepPath, setStepPath] = useState<string>("");
  // -- station generator (choose the slicing: start / end / count)
  const [genStart, setGenStart] = useState<number>(0.15);
  const [genEnd, setGenEnd] = useState<number>(0.9);
  const [genCount, setGenCount] = useState<number>(5);
  /** Toggle between the flat SVG planform sketch and the 3-D orbitable viewer. */
  const [show3d, setShow3d] = useState<boolean>(false);

  // -- wing identity + per-section local edits ------------------------------
  const [wingId, setWingId] = useState<string | null>(null);
  const [openSectionPath, setOpenSectionPath] = useState<string | null>(null);
  /** Locally-held foil configs keyed by section.path (the seam store). */
  const [sectionDesigns, setSectionDesigns] = useState<Record<string, HighLiftAirfoil>>({});
  /** The live foil design the 3-D viewer lofts across the span. Seeded from the
   *  default high-lift airfoil; the open section editor streams its edits here so
   *  the 3-D wing rebuilds as the 2-D foil/deploy is changed. */
  const [liveAirfoil, setLiveAirfoil] = useState<HighLiftAirfoil>(() =>
    structuredClone(DEFAULT_HIGH_LIFT_AIRFOIL),
  );

  /** Flap span band (eta) for the 3-D cove/vane/flap — spans the sliced stations. */
  const flapBand = useMemo<[number, number]>(() => {
    if (etaStations.length >= 2) {
      const sorted = [...etaStations].sort((a, b) => a - b);
      const lo = Math.max(0.02, Math.min(0.9, sorted[0]!));
      const hi = Math.min(0.98, Math.max(lo + 0.05, sorted[sorted.length - 1]!));
      return [lo, hi];
    }
    return [0.25, 0.7];
  }, [etaStations]);

  // -- carve panel state ----------------------------------------------------
  const [deployDeg, setDeployDeg] = useState<Record<string, number>>({});
  const [nLoft, setNLoft] = useState<number>(DEFAULT_N_LOFT);
  const [quality, setQuality] = useState<string>("fast");

  // -- sections (refreshed after slice / section edits) ---------------------
  const sectionsQuery = useQuery({
    queryKey: ["wing-sections", projectId, branch, wingId],
    queryFn: () => listSections(projectId, branch, wingId as string),
    enabled: Boolean(wingId) && persisted,
  });
  const sections: SectionSummary[] = sectionsQuery.data ?? [];

  const refreshSections = useCallback(() => {
    if (wingId) {
      void queryClient.invalidateQueries({ queryKey: ["wing-sections", projectId, branch, wingId] });
    }
  }, [branch, projectId, queryClient, wingId]);

  // -- mutations ------------------------------------------------------------
  const sliceMutation = useMutation({
    mutationFn: (): Promise<SliceResponse> =>
      sliceWing(projectId, branch, {
        step_path: stepPath.trim() || undefined,
        planform,
        eta_stations: [...etaStations].sort((a, b) => a - b),
        xc_hinge: xcHinge,
        n_extract: nExtract,
        name: "wing",
      }),
    onSuccess: (resp) => {
      setWingId(resp.wing_id);
      // Seed deploy inputs from the new sections so the carve panel is populated.
      setDeployDeg((prev) => {
        const next = { ...prev };
        for (const s of resp.sections) {
          const key = sectionEtaKey(s.eta);
          if (next[key] == null) next[key] = 0;
        }
        return next;
      });
      refreshSections();
    },
  });

  const saveMutation = useMutation({
    mutationFn: (args: { section: SectionSummary; config: HighLiftAirfoil }) =>
      upsertSections(projectId, branch, {
        wing_id: wingId,
        name: "wing",
        planform,
        sections: [
          {
            eta: args.section.eta,
            design: { [DESIGN_CONFIG_KEY]: args.config as unknown as Record<string, unknown> },
            design_path: args.section.linked_design_path ?? `${args.section.path}.design`,
            motion_law: args.section.motion_law ?? "simple_hinge",
          },
        ],
      }),
    onSuccess: (_resp, args) => {
      // Keep the local seam store in sync so re-open restores the edited design.
      setSectionDesigns((prev) => ({ ...prev, [args.section.path]: args.config }));
      setOpenSectionPath(null);
      refreshSections();
    },
  });

  const carveMutation = useMutation({
    mutationFn: (): Promise<CarveResponse> =>
      carveWing(projectId, branch, {
        wing_id: wingId as string,
        n_loft: nLoft,
        quality,
        deploy_deg: deployDeg,
      }),
  });

  // -- control-surface cut panel state ---------------------------------------
  const [csEta1, setCsEta1] = useState<number>(0.72);
  const [csEta2, setCsEta2] = useState<number>(0.95);
  const [csMode, setCsMode] = useState<CutMode>("gapped");
  const [csDeflections, setCsDeflections] = useState<string>("-20, 0, 20");
  const [csGapMm, setCsGapMm] = useState<number>(10);
  const [csRadiusMm, setCsRadiusMm] = useState<number>(25);

  const cutMutation = useMutation({
    mutationFn: (): Promise<CutResponse> => {
      const deflections = csDeflections
        .split(/[,\s]+/)
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));
      return cutControlSurface(projectId, {
        step_path: stepPath.trim(),
        eta1: csEta1,
        eta2: csEta2,
        xc_hinge: xcHinge,
        mode: csMode,
        deflections_deg: deflections.length ? deflections : [0],
        gap_mm: csGapMm,
        le_radius_mm: csRadiusMm,
        // design mode lofts the saved parameterised foil designs (vane + cove)
        wing_id: csMode === "design" ? (wingId ?? undefined) : undefined,
        branch,
      });
    },
  });
  const cut = cutMutation.data;

  // -- planform field editor ------------------------------------------------
  const setPlanformField = useCallback(
    (field: keyof WingPlanform, value: number) => setPlanform((p) => ({ ...p, [field]: value })),
    [],
  );

  const addStation = useCallback(() => {
    setEtaStations((prev) => {
      const next = [...prev];
      // Insert at the largest gap midpoint (or 0.5 when empty).
      const sorted = [...prev].sort((a, b) => a - b);
      let eta = 0.5;
      if (sorted.length >= 2) {
        let bestGap = -1;
        for (let i = 1; i < sorted.length; i++) {
          const gap = sorted[i]! - sorted[i - 1]!;
          if (gap > bestGap) {
            bestGap = gap;
            eta = (sorted[i]! + sorted[i - 1]!) / 2;
          }
        }
      } else if (sorted.length === 1) {
        eta = sorted[0]! < 0.5 ? Math.min(1, sorted[0]! + 0.25) : Math.max(0, sorted[0]! - 0.25);
      }
      next.push(Number(eta.toFixed(3)));
      return next;
    });
  }, []);

  const removeStation = useCallback((index: number) => {
    setEtaStations((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const changeStation = useCallback((index: number, eta: number) => {
    setEtaStations((prev) => prev.map((value, i) => (i === index ? Number(eta.toFixed(3)) : value)));
  }, []);

  const carve = carveMutation.data;

  // -- render ---------------------------------------------------------------
  return (
    <section
      aria-label="Wing sectioning"
      data-testid="wing-sectioning-surface"
      style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16, fontFamily: FONT_STACK }}
    >
      {!persisted ? (
        <div
          role="note"
          style={{
            padding: "10px 12px",
            borderRadius: 6,
            background: palette.surface2,
            border: `1px solid ${palette.warn}`,
            color: palette.ink,
            fontSize: 12,
          }}
        >
          Wing slicing &amp; carving need a saved Thread project. This view isn&apos;t a
          persisted project (its URL isn&apos;t a project id), so those actions are
          disabled — open the project from your projects list to enable them.
        </div>
      ) : null}
      {/* ── 1. Planform + stations ─────────────────────────────────────── */}
      <div style={panelStyle}>
        <div style={{ ...sectionTitleStyle, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>1 · Planform &amp; eta stations</span>
          {/* 2D ↔ 3D toggle */}
          <span style={{ display: "inline-flex", gap: 4 }}>
            <button
              type="button"
              style={{
                ...ghostButtonStyle,
                background: !show3d ? palette.primarySoft : "transparent",
                color: !show3d ? palette.primary : palette.ink3,
                border: `1px solid ${!show3d ? palette.primary : palette.line}`,
              }}
              onClick={() => setShow3d(false)}
              title="Flat SVG planform sketch"
            >
              2D
            </button>
            <button
              type="button"
              style={{
                ...ghostButtonStyle,
                background: show3d ? palette.primarySoft : "transparent",
                color: show3d ? palette.primary : palette.ink3,
                border: `1px solid ${show3d ? palette.primary : palette.line}`,
              }}
              onClick={() => setShow3d(true)}
              title="Orbitable 3D wing viewer — click a marker to open its 2D foil editor"
            >
              3D
            </button>
          </span>
        </div>
        {/* CAD source: slice an arbitrary STEP (uploaded aircraft/wing) instead
            of the parametric planform. Empty = parametric. */}
        <div data-tour="wing-cad-source" style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
          <label style={{ flex: 1, minWidth: 0 }}>
            <span style={fieldLabelStyle}>CAD source (STEP path — empty = parametric planform)</span>
            <input
              type="text"
              placeholder="/path/to/aircraft.stp — e.g. from a geometry upload"
              value={stepPath}
              onChange={(e) => setStepPath(e.target.value)}
              style={{ ...numberInputStyle, width: "100%", fontFamily: MONO_STACK }}
            />
          </label>
          {stepPath.trim() ? (
            <span style={{ fontSize: 11, color: palette.primary, whiteSpace: "nowrap", paddingBottom: 6 }}>
              slicing real CAD sections
            </span>
          ) : null}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(220px, 1fr)", gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            {show3d ? (
              /* ── 3-D orbitable wing viewer ─────────────────────────────────── */
              <div style={{ height: 280, position: "relative" }}>
                <WingViewer3D
                  planform={planform}
                  airfoil={liveAirfoil}
                  band={flapBand}
                  etaStations={etaStations}
                  sections={sections}
                  selectedEta={
                    openSectionPath
                      ? (sections.find((s) => s.path === openSectionPath)?.eta ?? null)
                      : null
                  }
                  onSelectStation={(eta) => {
                    // Find the section for this eta (if already sliced) and open its foil editor.
                    const section = sections.find((s) => Math.abs(s.eta - eta) < 0.0005);
                    if (section) {
                      setOpenSectionPath((prev) => (prev === section.path ? null : section.path));
                    } else {
                      // Station not yet sliced — just highlight it (no-op, slice first).
                      // Optionally we could scroll to the station chips here.
                    }
                  }}
                  palette={palette}
                />
              </div>
            ) : (
              /* ── Flat SVG planform sketch ───────────────────────────────────── */
              <PlanformSketch planform={planform} etaStations={etaStations} onChangeEta={changeStation} palette={palette} />
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, alignContent: "start" }}>
            {(
              [
                ["root_le_x", "root LE x"],
                ["tip_le_x", "tip LE x"],
                ["root_chord", "root chord"],
                ["tip_chord", "tip chord"],
                ["span", "span"],
                ["y_root", "y root"],
              ] as [keyof WingPlanform, string][]
            ).map(([field, label]) => (
              <label key={field}>
                <span style={fieldLabelStyle}>{label}</span>
                <input
                  type="number"
                  step="0.1"
                  value={planform[field]}
                  onChange={(e) => setPlanformField(field, readNumber(e.target.value, planform[field]))}
                  style={numberInputStyle}
                />
              </label>
            ))}
            <label>
              <span style={fieldLabelStyle}>x/c hinge</span>
              <input
                type="number"
                step="0.05"
                value={xcHinge}
                onChange={(e) => setXcHinge(readNumber(e.target.value, xcHinge))}
                style={numberInputStyle}
              />
            </label>
            <label>
              <span style={fieldLabelStyle}>n extract</span>
              <input
                type="number"
                step="1"
                value={nExtract}
                onChange={(e) => setNExtract(Math.round(readNumber(e.target.value, nExtract)))}
                style={numberInputStyle}
              />
            </label>
          </div>
        </div>

        {/* station chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 12 }}>
          {etaStations.map((eta, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: palette.surface2,
                border: `1px solid ${palette.line}`,
                borderRadius: 14,
                padding: "3px 6px 3px 10px",
                fontFamily: MONO_STACK,
                fontSize: 11,
                color: palette.ink,
              }}
            >
              η {eta.toFixed(3)}
              <button
                type="button"
                aria-label={`remove station ${eta.toFixed(3)}`}
                onClick={() => removeStation(i)}
                style={{ ...ghostButtonStyle, padding: "0 6px", border: "none", color: palette.ink3 }}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" style={ghostButtonStyle} onClick={addStation}>
            + station
          </button>
          {/* generator: choose the slicing — where to start/end + how many */}
          <span data-tour="wing-stations" style={{ display: "inline-flex", gap: 6, alignItems: "flex-end", marginLeft: 12 }}>
            <label>
              <span style={fieldLabelStyle}>η start</span>
              <input type="number" step="0.05" min={0} max={1} value={genStart}
                onChange={(e) => setGenStart(readNumber(e.target.value, genStart))}
                style={{ ...numberInputStyle, width: 64 }} />
            </label>
            <label>
              <span style={fieldLabelStyle}>η end</span>
              <input type="number" step="0.05" min={0} max={1} value={genEnd}
                onChange={(e) => setGenEnd(readNumber(e.target.value, genEnd))}
                style={{ ...numberInputStyle, width: 64 }} />
            </label>
            <label>
              <span style={fieldLabelStyle}>count</span>
              <input type="number" step="1" min={2} max={32} value={genCount}
                onChange={(e) => setGenCount(Math.round(readNumber(e.target.value, genCount)))}
                style={{ ...numberInputStyle, width: 56 }} />
            </label>
            <button
              type="button"
              style={ghostButtonStyle}
              title="Replace the stations with an even spread from start to end"
              onClick={() => {
                const lo = Math.max(0, Math.min(genStart, genEnd));
                const hi = Math.min(1, Math.max(genStart, genEnd));
                const n = Math.max(2, Math.min(32, genCount));
                setEtaStations(
                  Array.from({ length: n }, (_, i) =>
                    Number((lo + ((hi - lo) * i) / (n - 1)).toFixed(3)),
                  ),
                );
              }}
            >
              generate
            </button>
          </span>
        </div>

        <div data-tour="wing-slice" style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
          <button
            type="button"
            style={{ ...buttonStyle, opacity: sliceMutation.isPending || etaStations.length === 0 || !persisted ? 0.6 : 1 }}
            disabled={sliceMutation.isPending || etaStations.length === 0 || !persisted}
            onClick={() => sliceMutation.mutate()}
          >
            {sliceMutation.isPending ? "Slicing…" : "Slice wing"}
          </button>
          {wingId ? (
            <span style={{ fontSize: 11, color: palette.ink3, fontFamily: MONO_STACK }}>
              wing {wingId}
            </span>
          ) : null}
          {sliceMutation.isError ? (
            <span style={{ fontSize: 11, color: palette.warn }}>
              {(sliceMutation.error as Error)?.message ?? "Slice failed"}
            </span>
          ) : null}
        </div>
      </div>

      {/* ── 2. Sections table ──────────────────────────────────────────── */}
      <div data-tour="wing-sections" style={panelStyle}>
        <div style={sectionTitleStyle}>2 · Sections</div>
        {!wingId ? (
          <p style={{ fontSize: 12, color: palette.ink3, margin: 0 }}>
            Slice the wing to materialise spanwise sections.
          </p>
        ) : sectionsQuery.isLoading ? (
          <p style={{ fontSize: 12, color: palette.ink3, margin: 0 }}>Loading sections…</p>
        ) : sections.length === 0 ? (
          <p style={{ fontSize: 12, color: palette.ink3, margin: 0 }}>No sections yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th style={thStyle}>eta</th>
                  <th style={thStyle}>design hash</th>
                  <th style={thStyle}>has design</th>
                  <th style={thStyle}>solution</th>
                  <th style={thStyle}>linked design</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => {
                  const isOpen = openSectionPath === s.path;
                  return (
                    <tr key={s.path}>
                      <td style={thtdStyle}>{s.eta.toFixed(3)}</td>
                      <td style={thtdStyle}>{s.design_hash ? s.design_hash.slice(0, 10) : "—"}</td>
                      <td style={thtdStyle}>{s.has_design ? "yes" : "no"}</td>
                      <td style={thtdStyle}>{s.solution_status ?? "—"}</td>
                      <td style={thtdStyle}>{s.linked_design_path ?? "—"}</td>
                      <td style={thtdStyle}>
                        <button
                          type="button"
                          style={ghostButtonStyle}
                          onClick={() => setOpenSectionPath(isOpen ? null : s.path)}
                        >
                          {isOpen ? "Close foil tool" : "Open in foil tool"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* open section foil editor */}
        {openSectionPath
          ? (() => {
              const section = sections.find((s) => s.path === openSectionPath);
              if (!section) return null;
              const deployKey = sectionEtaKey(section.eta);
              return (
                <SectionFoilEditor
                  key={section.path}
                  section={section}
                  seedConfig={storedConfigFor(section, sectionDesigns)}
                  seedDeployDeg={deployDeg[deployKey]}
                  saving={saveMutation.isPending}
                  writable={persisted}
                  onSave={(config) => saveMutation.mutate({ section, config })}
                  onClose={() => setOpenSectionPath(null)}
                  onConfigChange={setLiveAirfoil}
                  palette={palette}
                  styles={styles}
                />
              );
            })()
          : null}
        {saveMutation.isError ? (
          <p style={{ fontSize: 11, color: palette.warn, marginTop: 8 }}>
            {(saveMutation.error as Error)?.message ?? "Save failed"}
          </p>
        ) : null}
      </div>

      {/* ── 3. Carve ───────────────────────────────────────────────────── */}
      <div style={panelStyle}>
        <div style={sectionTitleStyle}>3 · Carve (deploy &amp; loft)</div>
        {!wingId || sections.length === 0 ? (
          <p style={{ fontSize: 12, color: palette.ink3, margin: 0 }}>
            Slice the wing first to set per-station deploy angles.
          </p>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
              {sections.map((s) => {
                const key = sectionEtaKey(s.eta);
                return (
                  <label key={s.path}>
                    <span style={fieldLabelStyle}>η {s.eta.toFixed(3)} deploy°</span>
                    <input
                      type="number"
                      step="1"
                      value={deployDeg[key] ?? 0}
                      onChange={(e) =>
                        setDeployDeg((prev) => ({ ...prev, [key]: readNumber(e.target.value, prev[key] ?? 0) }))
                      }
                      style={numberInputStyle}
                    />
                  </label>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 12 }}>
              <label>
                <span style={fieldLabelStyle}>n loft</span>
                <input
                  type="number"
                  step="1"
                  value={nLoft}
                  onChange={(e) => setNLoft(Math.round(readNumber(e.target.value, nLoft)))}
                  style={{ ...numberInputStyle, width: 80 }}
                />
              </label>
              <label>
                <span style={fieldLabelStyle}>quality</span>
                <select
                  value={quality}
                  onChange={(e) => setQuality(e.target.value)}
                  style={{ ...numberInputStyle, width: 110 }}
                >
                  <option value="fast">fast</option>
                  <option value="medium">medium</option>
                  <option value="fine">fine</option>
                </select>
              </label>
              <button
                type="button"
                style={{ ...buttonStyle, opacity: carveMutation.isPending || !persisted ? 0.6 : 1 }}
                disabled={carveMutation.isPending || !persisted}
                onClick={() => carveMutation.mutate()}
              >
                {carveMutation.isPending ? "Carving…" : "Carve"}
              </button>
            </div>

            {/* honest carve result */}
            {carveMutation.isError ? (
              <p style={{ fontSize: 11, color: palette.warn, marginTop: 10 }}>
                {(carveMutation.error as Error)?.message ?? "Carve failed"}
              </p>
            ) : carve ? (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 6,
                  background: palette.surface2,
                  border: `1px solid ${palette.line}`,
                }}
              >
                {loftNotImplemented(carve) ? (
                  <>
                    <div style={{ fontSize: 12, color: palette.ink, fontWeight: 600 }}>
                      3D loft not yet wired — sections staged
                    </div>
                    <div style={{ fontSize: 11, color: palette.ink3, marginTop: 4 }}>
                      {carve.note
                        ? carve.note
                        : "The backend staged the per-station hinges & deploy schedule but did not produce a STEP body (loft_status=not_implemented)."}
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: palette.primary, fontWeight: 600 }}>3D body carved</div>
                    {carve.step_path ? (
                      <div style={{ fontSize: 11, color: palette.ink3, marginTop: 4, fontFamily: MONO_STACK }}>
                        STEP: {carve.step_path}
                      </div>
                    ) : null}
                    {carve.job_id ? (
                      <div style={{ fontSize: 11, color: palette.ink3, marginTop: 2, fontFamily: MONO_STACK }}>
                        job: {carve.job_id}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* ── 4. Control surface (cut into the CAD) ──────────────────────── */}
      <div data-tour="wing-cs-panel" style={panelStyle}>
        <div style={sectionTitleStyle}>4 · Control surface — cut into the CAD</div>
        {!stepPath.trim() ? (
          <p style={{ fontSize: 12, color: palette.ink3, margin: 0 }}>
            Set a CAD source (STEP path) in panel 1 — the cut works directly on the
            source solid: sections are sliced from it, the planform and hinge are
            fitted from those sections, and the aileron is cut out and pre-rotated.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
              <label>
                <span style={fieldLabelStyle}>η start</span>
                <input type="number" step="0.01" min={0} max={1} value={csEta1}
                  onChange={(e) => setCsEta1(readNumber(e.target.value, csEta1))}
                  style={{ ...numberInputStyle, width: 70 }} />
              </label>
              <label>
                <span style={fieldLabelStyle}>η end</span>
                <input type="number" step="0.01" min={0} max={1} value={csEta2}
                  onChange={(e) => setCsEta2(readNumber(e.target.value, csEta2))}
                  style={{ ...numberInputStyle, width: 70 }} />
              </label>
              <label>
                <span style={fieldLabelStyle}>mode</span>
                <select value={csMode} onChange={(e) => setCsMode(e.target.value as CutMode)}
                  style={{ ...numberInputStyle, width: 190 }}>
                  <option value="gapped">quick · gapped</option>
                  <option value="shaped">shaped · cove + spiral</option>
                  <option value="design" disabled={!wingId}>
                    design · vane + cove elements{wingId ? "" : " (slice + save designs first)"}
                  </option>
                  <option value="simple">quick · simple</option>
                  <option value="sealed">quick · sealed</option>
                </select>
              </label>
              <label>
                <span style={fieldLabelStyle}>deflections°</span>
                <input type="text" value={csDeflections}
                  onChange={(e) => setCsDeflections(e.target.value)}
                  style={{ ...numberInputStyle, width: 110, fontFamily: MONO_STACK }} />
              </label>
              <label>
                <span style={fieldLabelStyle}>gap mm</span>
                <input type="number" step="1" value={csGapMm}
                  onChange={(e) => setCsGapMm(readNumber(e.target.value, csGapMm))}
                  style={{ ...numberInputStyle, width: 64 }} />
              </label>
              <label>
                <span style={fieldLabelStyle}>LE radius mm</span>
                <input type="number" step="1" value={csRadiusMm}
                  onChange={(e) => setCsRadiusMm(readNumber(e.target.value, csRadiusMm))}
                  style={{ ...numberInputStyle, width: 64 }} />
              </label>
              <button
                type="button"
                style={{ ...buttonStyle, opacity: cutMutation.isPending || !persisted ? 0.6 : 1 }}
                disabled={cutMutation.isPending || !persisted}
                onClick={() => cutMutation.mutate()}
              >
                {cutMutation.isPending
                  ? (csMode === "shaped" ? "Cutting (shaped ~1 min)…" : "Cutting…")
                  : "Cut control surface"}
              </button>
            </div>
            <p style={{ fontSize: 11, color: palette.ink3, margin: "8px 0 0" }}>
              Hinge x/c comes from panel 1 ({xcHinge.toFixed(2)}). Quick modes cut a
              hinge-line box with LE cylinder + gap; shaped lofts the 2D editor&apos;s
              cove + spiral geometry across the span.
            </p>

            {cutMutation.isError ? (
              <p style={{ fontSize: 11, color: palette.warn, marginTop: 10 }}>
                {(cutMutation.error as Error)?.message ?? "Cut failed"}
              </p>
            ) : cut ? (
              <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 6,
                background: palette.surface2, border: `1px solid ${palette.line}` }}>
                <div style={{ fontSize: 12, color: palette.primary, fontWeight: 600 }}>
                  {cut.outputs.length}{" "}
                  {cut.mode === "design" ? "design output" : "deflection case"}
                  {cut.outputs.length === 1 ? "" : "s"} written · {cut.mode}
                </div>
                {cut.outputs.map((o) => (
                  <div key={o.step_path}
                    style={{ fontSize: 11, color: palette.ink3, marginTop: 4, fontFamily: MONO_STACK }}>
                    {o.deflection_deg == null
                      ? "design"
                      : `${o.deflection_deg >= 0 ? "+" : ""}${o.deflection_deg.toFixed(1)}°`} → {o.step_path}
                  </div>
                ))}
                {cut.planform && cut.planform.root_chord != null ? (
                  <div style={{ fontSize: 11, color: palette.ink3, marginTop: 6 }}>
                    planform fitted from sections: chord {cut.planform.root_chord?.toFixed(0)} → {cut.planform.tip_chord?.toFixed(0)}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
